import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight, Sparkles, PartyPopper } from 'lucide-react';
import { useOnboarding } from '../../context/OnboardingContext';
import './OnboardingOverlay.css';

const STEP_KEY_MAP = {
  'welcome': 'welcome',
  'sidebar': 'sidebar',
  'dashboard-stats': 'dashboardStats',
  'quick-actions': 'quickActions',
  'offers': 'offers',
  'requests': 'requests',
  'network': 'network',
  'catalog': 'catalog',
  'statistics': 'statistics',
  'settings': 'settings',
  'complete': 'complete',
};

const OnboardingOverlay = () => {
  const { t, i18n } = useTranslation();
  const {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    nextStep,
    prevStep,
    skipOnboarding,
  } = useOnboarding();

  const [spotlightRect, setSpotlightRect] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const tooltipRef = useRef(null);
  const isRTL = i18n.language === 'ar';

  const calculatePosition = useCallback(() => {
    if (!currentStep || !currentStep.target) {
      setSpotlightRect(null);
      setTooltipStyle({ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      return;
    }

    const el = document.querySelector(currentStep.target);
    if (!el) {
      setSpotlightRect(null);
      setTooltipStyle({ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      return;
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });

    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      const padding = 8;
      setSpotlightRect({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        borderRadius: 12,
      });

      const tooltipGap = 12;
      const tooltipWidth = 320;
      const tooltipEstimatedHeight = 220;
      const safeMargin = 16;
      const style = { position: 'fixed' };
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const spaceAbove = rect.top - padding;
      const spaceBelow = vh - rect.bottom - padding;

      if (vw < 768) {
        style.left = '50%';
        style.transform = 'translateX(-50%)';
        style.width = 'calc(100vw - 32px)';
        style.maxWidth = `${tooltipWidth}px`;
        if (spaceBelow >= tooltipEstimatedHeight + tooltipGap) {
          style.top = `${rect.bottom + padding + tooltipGap}px`;
        } else {
          style.bottom = `${vh - rect.top + padding + tooltipGap}px`;
        }
      } else {
        const pos = currentStep.position || 'bottom';
        switch (pos) {
          case 'bottom':
          case 'top': {
            let leftVal = rect.left + rect.width / 2 - tooltipWidth / 2;
            if (leftVal + tooltipWidth > vw - safeMargin) {
              leftVal = vw - tooltipWidth - safeMargin;
            }
            if (leftVal < safeMargin) {
              leftVal = safeMargin;
            }
            style.left = `${leftVal}px`;

            const preferBelow = pos === 'bottom';
            if (preferBelow && spaceBelow >= tooltipEstimatedHeight + tooltipGap) {
              style.top = `${rect.bottom + padding + tooltipGap}px`;
            } else if (!preferBelow && spaceAbove >= tooltipEstimatedHeight + tooltipGap) {
              style.bottom = `${vh - rect.top + padding + tooltipGap}px`;
            } else if (spaceBelow >= spaceAbove) {
              style.top = `${rect.bottom + padding + tooltipGap}px`;
            } else {
              style.bottom = `${vh - rect.top + padding + tooltipGap}px`;
            }
            break;
          }
          case 'left':
          case 'right': {
            let topVal = rect.top + rect.height / 2 - tooltipEstimatedHeight / 2;
            if (topVal + tooltipEstimatedHeight > vh - safeMargin) {
              topVal = vh - tooltipEstimatedHeight - safeMargin;
            }
            if (topVal < safeMargin) {
              topVal = safeMargin;
            }
            style.top = `${topVal}px`;

            if (pos === 'left') {
              if (isRTL) {
                style.right = `${vw - rect.left + padding + tooltipGap}px`;
              } else {
                style.left = `${rect.right + padding + tooltipGap}px`;
              }
            } else {
              if (isRTL) {
                style.left = `${rect.right + padding + tooltipGap}px`;
              } else {
                style.right = `${vw - rect.left + padding + tooltipGap}px`;
              }
            }
            break;
          }
          default:
            style.top = `${rect.bottom + padding + tooltipGap}px`;
            style.left = `${rect.left + rect.width / 2}px`;
            style.transform = 'translateX(-50%)';
        }
      }

      setTooltipStyle(style);
    });
  }, [currentStep, isRTL]);

  useEffect(() => {
    if (!isActive) return;
    calculatePosition();
    window.addEventListener('resize', calculatePosition);
    window.addEventListener('scroll', calculatePosition, true);
    return () => {
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition, true);
    };
  }, [isActive, calculatePosition]);

  useEffect(() => {
    if (isActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') skipOnboarding();
      if (e.key === 'ArrowRight') isRTL ? prevStep() : nextStep();
      if (e.key === 'ArrowLeft') isRTL ? nextStep() : prevStep();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, nextStep, prevStep, skipOnboarding, isRTL]);

  if (!isActive || !currentStep) return null;

  const stepKey = STEP_KEY_MAP[currentStep.id] || currentStep.id;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const isCenterStep = !currentStep.target;
  const progress = ((currentStepIndex + 1) / totalSteps) * 100;

  return createPortal(
    <div className="onboarding-overlay" role="dialog" aria-modal="true" aria-label={t('onboarding.welcome.title')}>
      {spotlightRect && (
        <svg className="onboarding-backdrop" viewBox={`0 0 ${window.innerWidth} ${window.innerHeight}`} preserveAspectRatio="none">
          <defs>
            <mask id="onboarding-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <rect
                x={spotlightRect.left}
                y={spotlightRect.top}
                width={spotlightRect.width}
                height={spotlightRect.height}
                rx={spotlightRect.borderRadius}
                ry={spotlightRect.borderRadius}
                fill="black"
              />
            </mask>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.6)" mask="url(#onboarding-mask)" />
        </svg>
      )}

      {!spotlightRect && (
        <div className="onboarding-backdrop-solid" />
      )}

      {spotlightRect && (
        <>
          <div
            className="onboarding-spotlight-ring"
            style={{
              top: spotlightRect.top - 3,
              left: spotlightRect.left - 3,
              width: spotlightRect.width + 6,
              height: spotlightRect.height + 6,
              borderRadius: spotlightRect.borderRadius + 3,
            }}
          />
          <div
            className="onboarding-spotlight-click"
            style={{
              position: 'fixed',
              top: spotlightRect.top - 3,
              left: spotlightRect.left - 3,
              width: spotlightRect.width + 6,
              height: spotlightRect.height + 6,
              borderRadius: spotlightRect.borderRadius + 3,
              zIndex: 200002,
              cursor: 'pointer',
            }}
            onClick={() => {
              const el = document.querySelector(currentStep.target);
              if (el) el.click();
              setTimeout(() => nextStep(), 350);
            }}
          />
        </>
      )}

      <div
        ref={tooltipRef}
        className={`onboarding-tooltip ${isCenterStep ? 'center-mode' : ''} ${isLastStep ? 'complete-mode' : ''}`}
        style={tooltipStyle}
      >
        {!isFirstStep && !isLastStep && (
          <button className="onboarding-skip-btn" onClick={skipOnboarding} aria-label={t('onboarding.skip')}>
            <X size={18} />
          </button>
        )}

        <div className="onboarding-tooltip-content">
          {isFirstStep && (
            <div className="onboarding-icon-welcome">
              <Sparkles size={40} />
            </div>
          )}
          {isLastStep && (
            <div className="onboarding-icon-complete">
              <PartyPopper size={40} />
            </div>
          )}

          <h3 className="onboarding-title">{t(`onboarding.${stepKey}.title`)}</h3>
          <p className="onboarding-description">{t(`onboarding.${stepKey}.description`)}</p>
          {currentStep.target && (
            <p className="onboarding-hint">{t('onboarding.clickHint')}</p>
          )}
        </div>

        <div className="onboarding-footer">
          <div className="onboarding-progress">
            <div className="onboarding-progress-bar">
              <div className="onboarding-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="onboarding-step-count">
              {t('onboarding.stepOf', { current: currentStepIndex + 1, total: totalSteps })}
            </span>
          </div>

          <div className="onboarding-actions">
            {isFirstStep ? (
              <>
                <button className="onboarding-btn secondary" onClick={skipOnboarding}>
                  {t('onboarding.skip')}
                </button>
                <button className="onboarding-btn primary" onClick={nextStep}>
                  {t('onboarding.next')}
                  {isRTL ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
                </button>
              </>
            ) : isLastStep ? (
              <button className="onboarding-btn primary wide" onClick={nextStep}>
                {t('onboarding.finish')}
              </button>
            ) : (
              <>
                {currentStepIndex > 0 && (
                  <button className="onboarding-btn secondary" onClick={prevStep}>
                    {isRTL ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                    {t('onboarding.back')}
                  </button>
                )}
                <button className="onboarding-btn primary" onClick={nextStep}>
                  {t('onboarding.next')}
                  {isRTL ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OnboardingOverlay;
